import { useState, useEffect } from 'react';
import DescuentosAutomaticos from '../service/CodigosDescuento';
import { calcularEdad, calcularPrecioFinal } from '../utils/precioTaller';

export const useDescuentos = (tallerId, taller, esVecino) => {
  const [descuentosDisponibles, setDescuentosDisponibles] = useState([]);
  const [preciosConDescuento, setPreciosConDescuento] = useState({});
  const [loading, setLoading] = useState(false);

  // ── Precios informativos para PasoPlan (sin persona concreta) ─────────────
  const calcularPreciosDisponibles = async () => {
    if (!tallerId || !taller || taller.esGratuito) return;

    try {
      setLoading(true);
      const res = await DescuentosAutomaticos.GetPorTaller(tallerId, true);
      setDescuentosDisponibles(res || []);

      const preciosCalculados = {};
      for (const plan of taller.modalidadPago) {
        const tieneAM          = (plan.valorAdultoMayor  ?? 0) > 0;
        const tieneVecino      = (plan.valorVecino        ?? 0) > 0;
        const tieneFuncionario = (plan.valorFuncionarios  ?? 0) > 0;

        preciosCalculados[plan.plan] = {
          valorParticular:            plan.valorParticular  || 0,
          valorAdultoMayor:           tieneAM          ? plan.valorAdultoMayor  : null,
          valorVecino:                tieneVecino      ? plan.valorVecino        : null,
          valorFuncionarios:          tieneFuncionario ? plan.valorFuncionarios  : null,
          matricula:                  plan.matricula   || 0,
          tieneDescuentosAutomaticos: !!(res && res.length > 0),
          tienePrecioAdultoMayor:     tieneAM,
          tienePrecioVecino:          tieneVecino,
          tienePrecioFuncionario:     tieneFuncionario,
          descuentosDisponibles:      res || []
        };
      }
      setPreciosConDescuento(preciosCalculados);
    } catch (error) {
      console.error('Error cargando precios disponibles:', error);
      setDescuentosDisponibles([]);
      setPreciosConDescuento({});
    } finally {
      setLoading(false);
    }
  };

  // ── Precios aplicados para PasoConfirmacion (con persona concreta) ─────────
  // Fuente única de verdad: usa calcularPrecioFinal para todos los casos.
  // El mismo objeto se usa para display Y para construir el item del carrito.
  const calcularPreciosAplicados = async (fechaNacimientoPersona) => {
    if (!tallerId || !taller || taller.esGratuito) return {};

    const edad = calcularEdad(fechaNacimientoPersona);
    const preciosAplicados = {};

    for (const plan of taller.modalidadPago) {
      const particular = plan.valorParticular || 0;
      const { precio, tipo, descuento } = calcularPrecioFinal(plan, edad, esVecino);

      // Descuentos automáticos: solo aplican sobre precio particular
      // (cuando no hay precio especial de vecino ni adulto mayor)
      let montoFinal               = precio;
      let descuentosAplicados      = [];
      let totalDescuentoAutomatico = 0;

      if (tipo === 'particular' && descuentosDisponibles.length > 0) {
        try {
          const res = await DescuentosAutomaticos.CalcularPrecio(tallerId, 'particular', plan.plan);
          if (res?.tieneDescuento) {
            montoFinal               = res.montoFinal;
            descuentosAplicados      = res.descuentosAplicados || [];
            totalDescuentoAutomatico = res.totalDescuento;
          }
        } catch (err) {
          console.error('Error en descuento automático:', err);
        }
      }

      preciosAplicados[plan.plan] = {
        // Precios
        montoOriginal:           particular,
        montoFinal,

        // Descuentos
        descuentoEspecial:       descuento,           // descuento por tipo (AM / vecino)
        totalDescuentoAutomatico,
        totalDescuento:          descuento + totalDescuentoAutomatico,
        descuentosAplicados,

        // Flags
        tieneDescuento:          descuento > 0 || totalDescuentoAutomatico > 0,
        tieneDescuentoVecino:    tipo === 'vecino',
        tieneDescuentoAuto:      totalDescuentoAutomatico > 0,

        // Metadata de la persona
        tipo,                    // 'adultoMayor' | 'vecino' | 'particular'
        esAdultoMayor:           edad !== null && edad >= 60,
        esVecino,
        edad
      };
    }

    return preciosAplicados;
  };

  useEffect(() => {
    if (taller && tallerId) calcularPreciosDisponibles();
  }, [tallerId, taller, esVecino]);

  return {
    descuentosDisponibles,
    preciosConDescuento,
    loading,
    calcularEdad,
    calcularPreciosAplicados
  };
};
